import React, { useEffect, useState } from 'react';
import { Star, AccountBalance, CreditCard, Apple, Google, OpenInNew } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

// Mapping aller EUR-Länder (Eurozone) mit ihren größten Banken
const EUR_ZONE_MAPPING: Record<string, { name: string, banks: string[] }> = {
  de: { name: 'Germany', banks: ['Sparkasse', 'Volksbank', 'ING', 'Deutsche Bank', 'Commerzbank'] },
  at: { name: 'Austria', banks: ['Raiffeisen', 'Erste Bank', 'Bank Austria', 'Bawag'] },
  be: { name: 'Belgium', banks: ['KBC', 'Belfius', 'BNP Paribas Fortis', 'ING'] },
  cy: { name: 'Cyprus', banks: ['Bank of Cyprus', 'Hellenic Bank', 'Eurobank'] },
  ee: { name: 'Estonia', banks: ['Swedbank', 'SEB', 'LHV Pank', 'Luminor'] },
  fi: { name: 'Finland', banks: ['OP Bank', 'Nordea', 'Danske Bank', 'S-Pankki'] },
  fr: { name: 'France', banks: ['BNP Paribas', 'Société Générale', 'Crédit Agricole', 'BoursoBank'] },
  gr: { name: 'Greece', banks: ['Piraeus Bank', 'National Bank', 'Alpha Bank', 'Eurobank'] },
  ie: { name: 'Ireland', banks: ['AIB', 'Bank of Ireland', 'PTSB', 'Revolut'] },
  it: { name: 'Italy', banks: ['Intesa Sanpaolo', 'UniCredit', 'Poste Italiane', 'Fineco'] },
  hr: { name: 'Croatia', banks: ['Zagrebačka banka', 'Privredna banka', 'OTP banka'] },
  lv: { name: 'Latvia', banks: ['Swedbank', 'SEB', 'Citadele', 'Luminor'] },
  lt: { name: 'Lithuania', banks: ['Swedbank', 'SEB', 'Revolut', 'Luminor'] },
  lu: { name: 'Luxembourg', banks: ['BCEE', 'BGL BNP Paribas', 'BIL', 'Post'] },
  mt: { name: 'Malta', banks: ['Bank of Valletta', 'HSBC', 'APS Bank'] },
  nl: { name: 'Netherlands', banks: ['ABN AMRO', 'ING', 'Rabobank', 'SNS Bank'] },
  pt: { name: 'Portugal', banks: ['CGD', 'Millennium bcp', 'Novo Banco', 'BPI'] },
  sk: { name: 'Slovakia', banks: ['Slovenská sporiteľňa', 'VÚB banka', 'Tatra banka'] },
  si: { name: 'Slovenia', banks: ['NLB', 'NKBM', 'SKB banka'] },
  es: { name: 'Spain', banks: ['Santander', 'BBVA', 'CaixaBank', 'Sabadell'] },
  mc: { name: 'Monaco', banks: ['CMB Monaco', 'CFM Indosuez', 'Julius Baer'] },
  default: { name: 'Eurozone', banks: ['Revolut', 'N26', 'Wise', 'Bunq', 'Qonto'] }
};

const TrustAndPayments = () => {
  const {t} = useTranslation();
  const [regionInfo, setRegionInfo] = useState(EUR_ZONE_MAPPING.default);

  useEffect(() => {
    // Browser locale detection
    const langCode = navigator.language.split('-')[0].toLowerCase();

    // Check if the detected language belongs to a EUR country in our mapping
    if (EUR_ZONE_MAPPING[langCode]) {
      setRegionInfo(EUR_ZONE_MAPPING[langCode]);
    } else {
      setRegionInfo(EUR_ZONE_MAPPING.default);
    }
  }, []);

  return (
      <section className="w-full max-w-[1600px] mx-auto px-6 md:px-8 py-8 grid grid-cols-1 md:grid-cols-2 gap-12">
        {/* Left: TrustPilot */}
        <div className="bg-white dark:bg-neutral-900/20 border border-neutral-200 dark:border-neutral-800 rounded-3xl p-8 flex flex-col items-center md:items-start justify-center text-center md:text-left shadow-xl transition-all">
          <div className="flex items-center gap-2 mb-4">
            <span className="text-light-text dark:text-white font-black text-2xl tracking-tight">{t('landing_page.trust_and_payments.trustpilot.title')}</span>
          </div>

          <div className="flex gap-1 mb-4">
            {[1, 2, 3, 4, 5].map((i) => (
                <div key={i} className="bg-[#00b67a] p-1 rounded-sm">
                  <Star className="text-white text-xl md:text-2xl" />
                </div>
            ))}
          </div>

          <p className="text-gray-500 dark:text-slate-400 text-lg mb-8" dangerouslySetInnerHTML={{__html: t('landing_page.trust_and_payments.trustpilot.description')}} />

          <div className="mt-auto w-full md:w-auto">
            <a
                href="https://www.trustpilot.com/review/moonskins.gg"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 w-full md:w-auto bg-neutral-100 dark:bg-neutral-800 hover:bg-neutral-200 dark:hover:bg-neutral-700 text-light-text dark:text-white font-bold py-3 px-6 rounded-xl transition-all group"
            >
              <span>{t('landing_page.trust_and_payments.trustpilot.button_text')}</span>
              <OpenInNew className="text-sm group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" sx={{ fontSize: 18 }} />
            </a>
          </div>
        </div>

        {/* Right: Payments */}
        <div className="bg-white dark:bg-neutral-900/20 border border-neutral-200 dark:border-neutral-800 rounded-3xl p-8 shadow-xl transition-all">
          <div className="flex flex-col gap-8">
            {/* TrueLayer Section */}
            <div>
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <AccountBalance className="text-dark-primary" />
                  <span className="text-light-text dark:text-white font-bold text-xl">{t('landing_page.trust_and_payments.payments.truelayer_title')}</span>
                </div>
                <span className="text-[10px] font-bold bg-blue-500/10 text-blue-600 dark:text-blue-500 px-2 py-1 rounded uppercase tracking-widest border border-blue-500/20">
                {t('landing_page.trust_and_payments.payments.eur_only')}
              </span>
              </div>

              <div className="flex flex-wrap gap-2">
                <span className="text-gray-400 dark:text-slate-500 text-sm w-full mb-1">{t('landing_page.trust_and_payments.payments.local_banks', {country: regionInfo.name})}</span>
                {regionInfo.banks.map((bank) => (
                    <span key={bank} className="bg-neutral-100 dark:bg-neutral-800 text-gray-600 dark:text-slate-300 px-3 py-1.5 rounded-lg text-xs font-medium border border-neutral-200 dark:border-neutral-700">
                  {bank}
                </span>
                ))}
                <span className="text-gray-400 dark:text-slate-500 text-xs self-center ml-1 italic">{t('landing_page.trust_and_payments.payments.others')}</span>
              </div>
            </div>

            {/* Cards & Wallets */}
            <div className="pt-6 border-t border-neutral-100 dark:border-neutral-800/50">
              <div className="flex flex-wrap items-center gap-6 opacity-60 hover:opacity-100 transition-opacity">
                <div className="flex items-center gap-2">
                  <CreditCard className="text-gray-600 dark:text-slate-300" />
                  <span className="text-sm font-bold text-gray-600 dark:text-slate-300 whitespace-nowrap">{t('landing_page.trust_and_payments.payments.cards_wallets')}</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <Apple className="text-gray-600 dark:text-slate-300 mb-1" />
                  <span className="text-sm font-bold text-gray-600 dark:text-slate-300 whitespace-nowrap">Apple Pay</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <Google className="text-gray-600 dark:text-slate-300" />
                  <span className="text-sm font-bold text-gray-600 dark:text-slate-300 whitespace-nowrap">Google Pay</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
  );
};

export default TrustAndPayments;